import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

export interface TeamMember {
  id: string;
  user_id: string;
  restaurant_id: string;
  role: AppRole;
  created_at: string;
  operator_pin?: string | null;
  email?: string;
  profile?: {
    full_name: string | null;
    phone: string | null;
  };
}

const roleLabels: Record<AppRole, string> = {
  super_admin: "Super Admin",
  restaurant_owner: "Proprietário",
  manager: "Gerente",
  cashier: "Caixa",
  waiter: "Garçom",
  kitchen: "Cozinha",
};

export function useTeam() {
  const { activeRestaurant } = useRestaurantContext();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTeam = useCallback(async () => {
    if (!activeRestaurant) {
      setIsLoading(false);
      return;
    }

    try {
      // Fetch team members with profiles
      const { data, error } = await supabase
        .from("restaurant_users")
        .select(`
          *,
          operator_pin,
          profile:profiles(full_name, phone)
        `)
        .eq("restaurant_id", activeRestaurant.id);

      if (error) throw error;

      // Fetch emails from edge function
      let userEmails: Record<string, string> = {};
      try {
        const { data: session } = await supabase.auth.getSession();
        if (session?.session?.access_token) {
          const response = await supabase.functions.invoke("get-team-emails", {
            body: { restaurant_id: activeRestaurant.id },
          });
          if (response.data?.userEmails) {
            userEmails = response.data.userEmails;
          }
        }
      } catch (emailError) {
        console.error("Error fetching emails:", emailError);
      }

      setMembers(
        data.map((m: any) => ({
          ...m,
          email: userEmails[m.user_id] || undefined,
          profile: m.profile || { full_name: null, phone: null },
        })) as TeamMember[]
      );
    } catch (error) {
      console.error("Error fetching team:", error);
      toast.error("Erro ao carregar equipe");
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant]);

  const removeMember = useCallback(async (memberId: string, memberName: string) => {
    try {
      const { error } = await supabase
        .from("restaurant_users")
        .delete()
        .eq("id", memberId);

      if (error) throw error;

      toast.success(`${memberName || "Membro"} removido da equipe`);
      fetchTeam();
      return true;
    } catch (error: any) {
      console.error("Error removing member:", error);
      toast.error("Erro ao remover membro da equipe");
      return false;
    }
  }, [fetchTeam]);

  const getRoleLabel = (role: AppRole) => roleLabels[role] || role;

  const getRoleOptions = () => {
    return Object.entries(roleLabels)
      .filter(([role]) => role !== "super_admin")
      .map(([value, label]) => ({ value: value as AppRole, label }));
  };

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  return {
    members,
    isLoading,
    fetchTeam,
    removeMember,
    getRoleLabel,
    getRoleOptions,
  };
}
